/**
 * Storage de guias e agências (admin). Persistido em localStorage.
 */

import { getAssociacaoById, saveAssociacao } from './associacoesStorage';

export type StatusCadastro = 'pendente' | 'aprovado' | 'recusado' | 'inativo';

/** Perfis alinhados ao cadastro de fornecedor (`TipoFornecedor` em solicitações / app). */
export type PerfilFornecedor =
  | 'GUIA'
  | 'TERAPEUTA'
  | 'COMERCIO_RESTAURANTE'
  | 'GESTOR_ATRATIVO'
  | 'ANFITRIAO_HOSPEDAGEM';

export interface Guia {
  id: string;
  nome: string;
  cpf: string;
  /** Data de nascimento (YYYY-MM-DD), usada no desempate de ranking. */
  dataNascimento?: string;
  email: string;
  telefone: string;
  chavePix: string;
  /** BASE = cidade (somente cidade para guia) */
  baseCidadeId: string;
  /** Alteração de cidade base solicitada pelo guia, aguardando aprovação do admin */
  baseCidadePendenteAprovacao?: string;
  imagemPerfil?: string;
  /** Fotos do trabalho como guia (até 4, ex.: passeios, atividades). */
  fotosTrabalhoGuia?: string[];
  /** Fotos do trabalho como terapeuta (até 4). */
  fotosTrabalhoTerapeuta?: string[];
  /** Anexo do certificado de primeiros socorros (data URL ou path) - @deprecated use certificados */
  certificadoAnexo?: string;
  /** Data de conclusão do certificado legado (ISO) - @deprecated use certificados[].dataConclusao */
  certificadoDataConclusao?: string;
  /** Certificados (GUIA e TERAPEUTA): tipo, descrição persistida, anexo, nome do ficheiro e data de conclusão opcional */
  certificados?: Array<{
    descricao: string;
    anexo: string;
    nomeArquivo?: string;
    dataConclusao?: string;
    /** @deprecated — migrado para dataConclusao */
    dataValidade?: string;
    tipoDocumento?: 'primeiros_socorros' | 'guia_condutor' | 'outros';
    outrosDetalhe?: string;
  }>;
  status: StatusCadastro;
  /** Motivo exibido ao parceiro quando o admin marca como inativo (sincronizado via API). */
  motivoInatividade?: string;
  /** Data limite de cadastro (definida manualmente ou legada; já não é calculada +2 anos). */
  dataLimiteCadastro?: string;
  /** Nota média de avaliação do guia (0 a 5). */
  notaAvaliacao?: number;
  agenciaId?: string;
  /** Perfis do fornecedor (mesmos valores da API / app). */
  perfis?: PerfilFornecedor[];
  /** Resumo de perfil profissional (GUIA e TERAPEUTA) */
  resumoPerfil?: string;
  /** ID do estabelecimento associado (COMÉRCIO / RESTAURANTE) */
  estabelecimentoId?: string;
  /** Anexo de documento de identificação (obrigatório quando associado a agência) */
  documentoIdentificacao?: string;
  /** RG / CNH / CPF (obrigatório para todo parceiro) */
  documentoIdentidadeFornecedor?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Agencia {
  id: string;
  cnpj: string;
  razaoSocial: string;
  /** Email da agência (obrigatório para novas) */
  email?: string;
  enderecoCompleto: string;
  telefone?: string;
  status: StatusCadastro;
  /** IDs dos guias que trabalham com a agência */
  guiaIds: string[];
  /** RG/CNH/documento do responsável (cadastro comércio via app). */
  documentoIdentidadeFornecedor?: string;
  createdAt: string;
  updatedAt: string;
}

const KEY_GUIAS = 'naviex-admin-guias';
const KEY_AGENCIAS = 'naviex-admin-agencias';
const ALERTA_MESES_ANTES = 3;

function certDataConclusaoFromRow(c: {
  dataConclusao?: string;
  dataValidade?: string;
}): string | undefined {
  const raw = ((c.dataConclusao ?? c.dataValidade) || '').trim().slice(0, 10);
  return raw || undefined;
}

function inferirTipoDocumentoCert(
  descricao: string,
  tipoStored?: 'primeiros_socorros' | 'guia_condutor' | 'outros'
): 'primeiros_socorros' | 'guia_condutor' | 'outros' {
  if (tipoStored) return tipoStored;
  const d = (descricao || '').toLowerCase();
  if (d.includes('primeiros')) return 'primeiros_socorros';
  if ((d.includes('guia') && d.includes('condutor')) || d.includes('condutor')) return 'guia_condutor';
  if (d.includes('conclusão') || d.includes('conclusao')) return 'guia_condutor';
  return 'outros';
}

/** Normaliza linhas de certificado (legado dataValidade → dataConclusao; infere tipo se faltar). */
export function normalizeGuiaCertificados(
  list: Guia['certificados']
): Guia['certificados'] | undefined {
  if (list == null) return undefined;
  if (list.length === 0) return [];
  return list.map((c) => {
    const dataConclusao = certDataConclusaoFromRow(c);
    const nomeArquivo = (c.nomeArquivo || '').trim() || undefined;
    const tipoDocumento = inferirTipoDocumentoCert(c.descricao, c.tipoDocumento);
    const outrosDetalhe =
      tipoDocumento === 'outros'
        ? (c.outrosDetalhe || '').trim() || (c.descricao || '').trim() || undefined
        : undefined;
    return {
      descricao: c.descricao,
      anexo: c.anexo,
      tipoDocumento,
      ...(tipoDocumento === 'outros' && outrosDetalhe ? { outrosDetalhe } : {}),
      ...(nomeArquivo ? { nomeArquivo } : {}),
      ...(dataConclusao ? { dataConclusao } : {}),
    };
  });
}

function parseDateOnly(dateInput: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateInput);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) return d;
    return null;
  }
  const parsed = new Date(dateInput);
  if (isNaN(parsed.getTime())) return null;
  return parsed;
}

function formatDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function perfisGuiaEfetivosStorage(g: Guia): PerfilFornecedor[] {
  if (g.perfis && g.perfis.length > 0) return [...g.perfis];
  return ['GUIA'];
}

function isPrimeirosSocorrosCertRow(c: NonNullable<Guia['certificados']>[number]): boolean {
  return c.tipoDocumento === 'primeiros_socorros' || /primeiros/i.test(c.descricao || '');
}

/**
 * Data de validade do certificado de primeiros socorros (YYYY-MM-DD):
 * data de conclusão + 2 anos + 1 dia (calendário local).
 * Ex.: conclusão 2025-07-23 → validade 2027-07-24.
 */
export function calcularDataValidadePrimeirosSocorros(dataConclusaoYmd: string): string | undefined {
  const d = parseDateOnly(dataConclusaoYmd.trim().slice(0, 10));
  if (!d || isNaN(d.getTime())) return undefined;
  const out = new Date(d.getFullYear() + 2, d.getMonth(), d.getDate());
  out.setDate(out.getDate() + 1);
  return formatDateOnly(out);
}

/** Só perfil GUIA: validade automática a partir do certificado de primeiros socorros (ou legado). */
export function getDataValidadePrimeirosSocorrosIsoGuia(g: Guia): string | undefined {
  if (!perfisGuiaEfetivosStorage(g).includes('GUIA')) return undefined;
  const certs = normalizeGuiaCertificados(g.certificados) ?? [];
  const ps = certs.find((c) => isPrimeirosSocorrosCertRow(c) && (c.dataConclusao || '').trim());
  const dc = ps?.dataConclusao?.trim().slice(0, 10);
  if (dc) return calcularDataValidadePrimeirosSocorros(dc);
  const leg = (g.certificadoDataConclusao ?? '').trim().slice(0, 10);
  const legAnexo = (g.certificadoAnexo ?? '').trim();
  if (leg && legAnexo) return calcularDataValidadePrimeirosSocorros(leg);
  return undefined;
}

function startOfTodayLocal(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function aplicarInativacaoPorVencimentoPrimeirosSocorros(list: Guia[]): { list: Guia[]; alterou: boolean } {
  const hoje = startOfTodayLocal();
  let alterou = false;
  const next = list.map((g) => {
    if (g.status !== 'aprovado') return g;
    if (!perfisGuiaEfetivosStorage(g).includes('GUIA')) return g;
    const validade = getDataValidadePrimeirosSocorrosIsoGuia(g);
    if (!validade) return g;
    const fim = parseDateOnly(validade);
    if (!fim || isNaN(fim.getTime())) return g;
    const primeiroDiaInativo = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate());
    primeiroDiaInativo.setDate(primeiroDiaInativo.getDate() + 1);
    if (hoje.getTime() < primeiroDiaInativo.getTime()) return g;
    alterou = true;
    return {
      ...g,
      status: 'inativo' as StatusCadastro,
      motivoInatividade: 'Certificado de primeiros socorros vencido (validade automática).',
    };
  });
  return { list: alterou ? next : list, alterou };
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const s = localStorage.getItem(key);
    return s ? (JSON.parse(s) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, data: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // ignore
  }
}

/** Retorna true se a data de validade (YYYY-MM-DD) ainda não passou (fim do dia da validade). */
export function certificadoValido(dataValidade?: string): boolean {
  if (!dataValidade) return false;
  const vencimento = certificadoVencimento(dataValidade);
  if (isNaN(vencimento.getTime())) return false;
  const hoje = new Date();
  return hoje <= vencimento;
}

/** Fim do dia da data de validade informada (YYYY-MM-DD). */
export function certificadoVencimento(dataValidade: string): Date {
  const d = parseDateOnly(dataValidade) ?? new Date(NaN);
  if (isNaN(d.getTime())) return d;
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * @deprecated Mantido para compatibilidade: devolve a mesma data (YYYY-MM-DD) sem somar anos.
 * Antes somava 2 anos a partir da “data de conclusão” de primeiros socorros.
 */
export function calcularDataLimiteCadastro(data?: string): string | undefined {
  if (!data) return undefined;
  const d = parseDateOnly(data.trim().slice(0, 10));
  if (!d || isNaN(d.getTime())) return undefined;
  return formatDateOnly(d);
}

/** Guia apto a guiar = aprovado + não inativo. Certificado de primeiros socorros opcional por enquanto. */
export function guiaApto(g: Guia): boolean {
  if (g.status === 'inativo' || g.status === 'recusado' || g.status === 'pendente') return false;
  return true;
}

/** Retorna true se o certificado vence em até 3 meses (para alerta). */
export function certificadoVenceEmBreve(dataValidade?: string): boolean {
  if (!dataValidade) return false;
  const venc = certificadoVencimento(dataValidade);
  if (isNaN(venc.getTime())) return false;
  const alerta = new Date();
  alerta.setHours(0, 0, 0, 0);
  alerta.setMonth(alerta.getMonth() + ALERTA_MESES_ANTES);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return venc <= alerta && hoje <= venc;
}

function migrarPerfisGuia(g: Guia): { guia: Guia; alterou: boolean } {
  const perfis = g.perfis;
  if (!perfis?.length) return { guia: g, alterou: false };
  let alterou = false;
  const next = perfis.map((p) => {
    if ((p as string) === 'EMPRESARIO') {
      alterou = true;
      return 'COMERCIO_RESTAURANTE' as PerfilFornecedor;
    }
    return p;
  });
  return alterou ? { guia: { ...g, perfis: next }, alterou: true } : { guia: g, alterou: false };
}

// ——— Guias ———
export function getGuias(): Guia[] {
  const raw = loadJson<Guia[]>(KEY_GUIAS, []);
  let precisaSalvar = false;
  const list = raw.map((g) => {
    const { guia: gMig, alterou } = migrarPerfisGuia(g);
    let guia = gMig;
    const certsNorm = normalizeGuiaCertificados(guia.certificados);
    if (certsNorm && JSON.stringify(certsNorm) !== JSON.stringify(guia.certificados)) {
      guia = { ...guia, certificados: certsNorm };
      precisaSalvar = true;
    }
    if (alterou) precisaSalvar = true;
    return guia;
  });
  const { list: listAposInat, alterou: alterouInat } = aplicarInativacaoPorVencimentoPrimeirosSocorros(list);
  if (alterouInat) precisaSalvar = true;
  if (precisaSalvar) saveJson(KEY_GUIAS, listAposInat);
  return listAposInat;
}

export function getGuiaById(id: string): Guia | null {
  return getGuias().find((g) => g.id === id) ?? null;
}

export function getGuiasByAgencia(agenciaId: string): Guia[] {
  return getGuias().filter((g) => g.agenciaId === agenciaId);
}

export function saveGuia(guia: Guia): void {
  const list = getGuias();
  const i = list.findIndex((g) => g.id === guia.id);
  const now = new Date().toISOString();
  const toSave = {
    ...guia,
    certificados: normalizeGuiaCertificados(guia.certificados),
    updatedAt: now,
  };
  if (i >= 0) list[i] = toSave;
  else list.push({ ...toSave, createdAt: now });
  saveJson(KEY_GUIAS, list);
}

export function addGuia(data: Omit<Guia, 'id' | 'createdAt' | 'updatedAt'>): Guia {
  const now = new Date().toISOString();
  const id = `guia-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const guia: Guia = {
    ...data,
    certificados: normalizeGuiaCertificados((data as Guia).certificados),
    id,
    createdAt: now,
    updatedAt: now,
  };
  const list = getGuias();
  list.push(guia);
  saveJson(KEY_GUIAS, list);
  return guia;
}

export function removeGuia(id: string): void {
  saveJson(KEY_GUIAS, getGuias().filter((g) => g.id !== id));
}

export function setGuiaStatus(id: string, status: StatusCadastro): void {
  const g = getGuiaById(id);
  if (!g) return;
  saveGuia({ ...g, status });
}

// ——— Agências ———
export function getAgencias(): Agencia[] {
  return loadJson<Agencia[]>(KEY_AGENCIAS, []);
}

export function getAgenciaById(id: string): Agencia | null {
  return getAgencias().find((a) => a.id === id) ?? null;
}

export function saveAgencia(agencia: Agencia): void {
  const list = getAgencias();
  const i = list.findIndex((a) => a.id === agencia.id);
  const now = new Date().toISOString();
  const toSave = { ...agencia, updatedAt: now };
  if (i >= 0) list[i] = toSave;
  else list.push({ ...toSave, createdAt: now });
  saveJson(KEY_AGENCIAS, list);
}

export function addAgencia(data: Omit<Agencia, 'id' | 'createdAt' | 'updatedAt'>): Agencia {
  const now = new Date().toISOString();
  const id = `ag-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const agencia: Agencia = { ...data, email: data.email ?? '', id, createdAt: now, updatedAt: now };
  const list = getAgencias();
  list.push(agencia);
  saveJson(KEY_AGENCIAS, list);
  return agencia;
}

export function setAgenciaStatus(id: string, status: StatusCadastro): void {
  const a = getAgenciaById(id);
  if (!a) return;
  saveAgencia({ ...a, status });
}

/** Atualiza a lista de guias da agência. */
export function setAgenciaGuias(agenciaId: string, guiaIds: string[]): void {
  const a = getAgenciaById(agenciaId);
  if (!a) return;
  saveAgencia({ ...a, guiaIds: [...guiaIds] });
}

/** Remove a agência e desvincula guias que apontavam para ela. */
export function removeAgencia(id: string): void {
  if (!getAgenciaById(id)) return;
  for (const g of getGuias()) {
    if (g.agenciaId === id) saveGuia({ ...g, agenciaId: undefined });
  }
  const list = getAgencias().filter((x) => x.id !== id);
  saveJson(KEY_AGENCIAS, list);
}

/** Vincula guia a agência ou associação (campo <c>guia.agenciaId</c> guarda o id de qualquer um). */
export function vincularGuiaAgencia(guiaId: string, agenciaId: string | null): void {
  const guia = getGuiaById(guiaId);
  if (!guia) return;
  if (guia.agenciaId) {
    const assPrev = getAssociacaoById(guia.agenciaId);
    if (assPrev)
      saveAssociacao({ ...assPrev, guiaIds: assPrev.guiaIds.filter((id) => id !== guiaId) });
    else {
      const ag = getAgenciaById(guia.agenciaId);
      if (ag) saveAgencia({ ...ag, guiaIds: ag.guiaIds.filter((id) => id !== guiaId) });
    }
  }
  saveGuia({ ...guia, agenciaId: agenciaId ?? undefined });
  if (agenciaId) {
    const ass = getAssociacaoById(agenciaId);
    if (ass && !ass.guiaIds.includes(guiaId))
      saveAssociacao({ ...ass, guiaIds: [...ass.guiaIds, guiaId] });
    else {
      const ag = getAgenciaById(agenciaId);
      if (ag && !ag.guiaIds.includes(guiaId))
        saveAgencia({ ...ag, guiaIds: [...ag.guiaIds, guiaId] });
    }
  }
}
